import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";
import Logo from "@/components/site/Logo";

export default function AdminResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setToken(params.get("token") || "");
    }, [params]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password updated — please sign in");
            navigate("/admin/login", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2">
            <div className="hidden lg:block relative bg-[#18181a] border-r border-white/10">
                <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: "url(https://images.pexels.com/photos/33814734/pexels-photo-33814734.jpeg?auto=compress&cs=tinysrgb&w=1200)" }} />
                <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-black/90" />
                <div className="absolute top-0 left-0 w-full h-2 hazard-stripes opacity-80" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12">
                    <Logo variant="compact" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-2">Reset Password</div>
                        <h1 className="font-display uppercase text-5xl leading-none">Choose a<br />new password.</h1>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md bg-[#18181a] border border-white/10 p-8" data-testid="reset-password-form">
                    <Link to="/admin/login" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white mb-8">
                        <ArrowLeft className="w-3 h-3" /> Back to login
                    </Link>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-[#d52b1e] text-white w-10 h-10 flex items-center justify-center">
                            <KeyRound className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">New password</div>
                            <div className="font-display uppercase text-2xl">Reset password</div>
                        </div>
                    </div>

                    {!token && (
                        <p className="text-sm text-red-400 mb-4" data-testid="reset-missing-token">
                            No reset token found. Please use the link from the email.
                        </p>
                    )}

                    <div className="space-y-7">
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="New password (min 8 chars)"
                            className="industrial-input"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            data-testid="reset-password-input"
                        />
                        <input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Confirm new password"
                            className="industrial-input"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            data-testid="reset-confirm-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting || !token}
                        className="mt-8 w-full bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                        data-testid="reset-submit-btn"
                    >
                        {submitting ? "Saving..." : "Set new password"}
                    </button>
                </form>
            </div>
        </div>
    );
}
